import { Inject, Injectable, Optional } from '@angular/core';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, map, pairwise, take, takeUntil } from 'rxjs/operators';

import { AccountSelectors } from '@core/account-data';
import { getProductCodenameFromSubscription } from '@core/billing-plans-legacy';
import { ENVIRONMENT, filterBoolean } from '@core/common-legacy';
import type { Company } from '@core/company-types';
import { I18nService } from '@core/i18n-legacy';
import type { IntercomEnvironment } from '@core/intercom-integration-legacy';
import { IntercomService } from '@core/intercom-integration-legacy';
import type { SupportedLanguage } from '@core/languages-legacy';
import { BillingPlanTier } from '@core/products-types';
import type { PricingFeatureSubscription } from '@core/subscription-types';

type LocalisedSurvey = Record<SupportedLanguage, number>;

function getProductCodename(currentCompany: Company): BillingPlanTier | null {
  const currentCompanySubscription = <PricingFeatureSubscription>currentCompany!.subscription;

  return (currentCompany && getProductCodenameFromSubscription(currentCompanySubscription)) || null;
}

@Injectable()
export class ProductOrderTrackingService {
  private ngOnDestroy$: Subject<void> = new Subject<void>();
  private launchToScaleUpgrade$ = this.store.select(AccountSelectors.currentCompany).pipe(
    filterBoolean,
    map(currentCompany => getProductCodename(currentCompany)),
    pairwise(),
    filter(
      ([prevProductCode, currProductCode]) =>
        prevProductCode === BillingPlanTier.Launch && currProductCode === BillingPlanTier.Scale,
    ),
  );

  constructor(
    private store: Store,
    private intercomService: IntercomService,
    private i18n: I18nService,
    @Optional()
    @Inject(ENVIRONMENT)
    private environment?: IntercomEnvironment,
  ) {
    this.launchToScaleUpgrade$.pipe(takeUntil(this.ngOnDestroy$), take(1)).subscribe(() => {
      const localisedSurvey = this.environment?.intercom?.surveys?.pricing;
      if (!localisedSurvey) return;

      const surveyId = localisedSurvey[<keyof LocalisedSurvey>this.i18n.locale];
      if (!surveyId) return;

      this.intercomService.scheduleSurvey(surveyId);
    });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
}
